/**
 * Product recommendation helpers — HONEST, catalog-only.
 *
 * Rules the caller MUST follow (guardrails from the redesign spec):
 *   * Never imply therapeutic combinations, protocols, benefits, or human
 *     use.  Copy on the surface must be operational ("Often sourced
 *     together") — not "you might also like" or "for X condition".
 *   * Never auto-add anything.  Each surface must render an explicit
 *     `Add` button; the user must always take an action.
 *   * Exclude products already in the cart AND the current product itself.
 *   * Recommend by SAME chemistry category first, then in-stock,
 *     purchasable, and public-price-visible.
 *   * Hard cap: 3 recommendations on PDP, 2 in the mini-cart drawer.
 *
 * These are pure functions — no side effects, no network calls.
 */

/** Purchasable === identical to `ProductPurchaseActions.isPurchasable`. */
const _purchasable = (p) =>
  !!(p && p.active && p.ecommerce_enabled &&
     p.checkout_status === "Add to Cart" &&
     p.card_payment_allowed &&
     p.public_price_visible &&
     (p.retail_price_cents || 0) > 0);

/**
 * Pick related products for a given seed product.
 *
 * @param {object}   seed          Product currently on PDP or last in cart.
 * @param {object[]} candidates    Full catalog (or a large enough slice).
 * @param {string[]} excludeSlugs  Slugs already in cart (never recommend).
 * @param {number}   max           Hard cap (3 for PDP, 2 for mini-cart).
 * @returns {object[]}             Up to `max` products in a stable order.
 */
export function pickRelatedProducts(seed, candidates, excludeSlugs = [], max = 3) {
  if (!seed?.slug || !Array.isArray(candidates) || max <= 0) return [];
  const exclude = new Set([seed.slug, ...(excludeSlugs || [])].filter(Boolean));
  const seedCat = seed.category_slug || seed.category || "";

  const pool = candidates.filter(
    (p) => p && p.slug && !exclude.has(p.slug) && _purchasable(p),
  );

  // Primary: same chemistry category as the seed.  Secondary fallback:
  // any in-stock, purchasable product (still no cross-category "protocols").
  const primary = seedCat
    ? pool.filter((p) => (p.category_slug || p.category) === seedCat)
    : [];
  const secondary = pool.filter((p) => !primary.includes(p));

  // Sort each group by explicit is_featured then popularity_rank if
  // present, else by price ascending, then slug alphabetical for
  // determinism (never invented "trending" scoring).
  const _sort = (arr) => [...arr].sort((a, b) => {
    if (!!b.is_featured - !!a.is_featured !== 0) return !!b.is_featured - !!a.is_featured;
    const pa = a.retail_price_cents ?? Number.MAX_SAFE_INTEGER;
    const pb = b.retail_price_cents ?? Number.MAX_SAFE_INTEGER;
    if (pa !== pb) return pa - pb;
    return String(a.slug).localeCompare(String(b.slug));
  });

  return [..._sort(primary), ..._sort(secondary)].slice(0, max);
}

/**
 * Extract slugs from a cart-items array as an exclusion set — the shape
 * matches what `useCart().items` provides.
 */
export function slugsInCart(cartItems) {
  return (cartItems || []).map((i) => i?.slug).filter(Boolean);
}
